import { useEffect, useRef, useState } from "react";
import type { CmmdComment } from "@cmmd-forge/contracts";

import { cmmdApiClient } from "~/environments/cmmdApi";
import { toastManager } from "../ui/toast";

export function useTaskDetailComments(cmmdTaskId: string | null, cmmdTaskNumber: number | null) {
  const [comments, setComments] = useState<ReadonlyArray<CmmdComment>>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const activeTaskIdRef = useRef(cmmdTaskId);
  activeTaskIdRef.current = cmmdTaskId;
  const editIdempotencyKeysRef = useRef(new Map<string, string>());
  const deleteIdempotencyKeysRef = useRef(new Map<string, string>());

  useEffect(() => {
    setComments([]);
    setDraft("");
    setPosting(false);
    setEditingId(null);
    setDeletingId(null);
    if (!cmmdTaskId) return;
    let cancelled = false;
    setLoading(true);
    void cmmdApiClient
      .getTaskComments(cmmdTaskId)
      .then((data) => {
        if (!cancelled) setComments(data ?? []);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cmmdTaskId]);

  const beginEdit = (comment: CmmdComment) => {
    setEditingId(comment.id);
    setEditingDraft(comment.content);
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = (comment: CmmdComment) => {
    if (!cmmdTaskNumber || !comment.commentKey || !editingDraft.trim()) return;
    const mutationTaskId = cmmdTaskId;
    const content = editingDraft.trim();
    const operationKey = `${cmmdTaskNumber}:${comment.commentKey}:${content}`;
    const idempotencyKey = editIdempotencyKeysRef.current.get(operationKey) ?? crypto.randomUUID();
    editIdempotencyKeysRef.current.set(operationKey, idempotencyKey);
    const previousContent = comment.content;
    setComments((current) =>
      current.map((item) => (item.id === comment.id ? { ...item, content } : item)),
    );
    setEditingId(null);
    void cmmdApiClient
      .updateProjectTaskComment(cmmdTaskNumber, comment.commentKey, {
        content,
        idempotencyKey,
      })
      .then(() => editIdempotencyKeysRef.current.delete(operationKey))
      .catch(() => {
        if (activeTaskIdRef.current === mutationTaskId) {
          setComments((current) =>
            current.map((item) =>
              item.id === comment.id && item.content === content
                ? { ...item, content: previousContent }
                : item,
            ),
          );
          toastManager.add({ type: "error", title: "Edit failed" });
        }
      });
  };
  const deleteComment = (comment: CmmdComment) => {
    if (!cmmdTaskNumber || !comment.commentKey) return;
    const mutationTaskId = cmmdTaskId;
    const operationKey = `${cmmdTaskNumber}:${comment.commentKey}`;
    const idempotencyKey =
      deleteIdempotencyKeysRef.current.get(operationKey) ?? crypto.randomUUID();
    deleteIdempotencyKeysRef.current.set(operationKey, idempotencyKey);
    const deletedIndex = comments.findIndex((item) => item.id === comment.id);
    setDeletingId(comment.id);
    setComments((current) => current.filter((item) => item.id !== comment.id));
    void cmmdApiClient
      .deleteProjectTaskComment(cmmdTaskNumber, comment.commentKey, {
        idempotencyKey,
      })
      .then(() => deleteIdempotencyKeysRef.current.delete(operationKey))
      .catch(() => {
        if (activeTaskIdRef.current === mutationTaskId) {
          setComments((current) => {
            if (current.some((item) => item.id === comment.id)) return current;
            const restored = [...current];
            restored.splice(Math.max(0, Math.min(deletedIndex, restored.length)), 0, comment);
            return restored;
          });
          toastManager.add({ type: "error", title: "Delete failed" });
        }
      })
      .finally(() => {
        if (activeTaskIdRef.current === mutationTaskId) {
          setDeletingId((current) => (current === comment.id ? null : current));
        }
      });
  };
  const post = () => {
    const content = draft.trim();
    if (!content) {
      toastManager.add({ type: "warning", title: "Comment cannot be empty." });
      return;
    }
    if (!cmmdTaskId) return;
    const mutationTaskId = cmmdTaskId;
    const optimisticId = `optimistic-${Date.now()}`;
    setComments((current) => [
      ...current,
      {
        id: optimisticId,
        content,
        authorId: "",
        authorName: "You",
        entityId: cmmdTaskId,
        entityType: "task",
        createdAt: new Date().toISOString(),
        updatedAt: null,
      },
    ]);
    setDraft("");
    setPosting(true);
    void cmmdApiClient
      .createComment({ entityType: "task", entityId: cmmdTaskId, content })
      .then(() => {
        toastManager.add({ type: "success", title: "Comment posted" });
        return cmmdApiClient.getTaskComments(cmmdTaskId);
      })
      .then((data) => {
        if (activeTaskIdRef.current === mutationTaskId) setComments(data ?? []);
      })
      .catch((error: unknown) => {
        if (activeTaskIdRef.current === mutationTaskId) {
          setComments((current) => current.filter((comment) => comment.id !== optimisticId));
          setDraft(content);
          toastManager.add({
            type: "error",
            title: "Failed to post comment",
            description: error instanceof Error ? error.message : undefined,
          });
        }
      })
      .finally(() => {
        if (activeTaskIdRef.current === mutationTaskId) setPosting(false);
      });
  };

  return {
    comments,
    loading,
    draft,
    setDraft,
    posting,
    editingId,
    editingDraft,
    setEditingDraft,
    deletingId,
    beginEdit,
    cancelEdit,
    saveEdit,
    deleteComment,
    post,
  } as const;
}
